import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

export default function ClubEditPlayer() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const teamId = searchParams.get('team');
  const navigate = useNavigate();
  const { apiFetch } = useAuth();

  const [player, setPlayer] = useState(null);
  const [loading, setLoading] = useState(true);

  // Name form
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [nameSaving, setNameSaving] = useState(false);
  const [nameError, setNameError] = useState('');
  const [nameSuccess, setNameSuccess] = useState('');

  // Parent email form
  const [parentEmail, setParentEmail] = useState('');
  const [emailSaving, setEmailSaving] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [emailSuccess, setEmailSuccess] = useState('');

  // Password form
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pwSaving, setPwSaving] = useState(false);
  const [pwError, setPwError] = useState('');
  const [pwSuccess, setPwSuccess] = useState('');

  useEffect(() => {
    if (!teamId) return;
    const loadPlayer = async () => {
      try {
        const data = await apiFetch('/api/admin/players', {
          headers: { 'x-team-id': teamId },
        });
        const found = data.players.find(p => p.id === id);
        if (found) {
          setPlayer(found);
          setFirstName(found.first_name || '');
          setLastName(found.last_name || '');
          setParentEmail(found.parent_email || '');
        }
      } catch (err) {
        console.error('Failed to load player:', err);
      } finally {
        setLoading(false);
      }
    };
    loadPlayer();
  }, [id, teamId, apiFetch]);

  const handleSaveName = async (e) => {
    e.preventDefault();
    setNameError(''); setNameSuccess('');
    if (!firstName.trim() || !lastName.trim()) {
      setNameError('First name and last name are required');
      return;
    }
    setNameSaving(true);
    try {
      await apiFetch(`/api/admin/players/${id}/name`, {
        method: 'PUT',
        headers: { 'x-team-id': teamId },
        body: JSON.stringify({ first_name: firstName.trim(), last_name: lastName.trim() }),
      });
      setNameSuccess('Name updated.');
    } catch (err) {
      setNameError(err.message);
    } finally {
      setNameSaving(false);
    }
  };

  const handleSaveEmail = async (e) => {
    e.preventDefault();
    setEmailError(''); setEmailSuccess('');
    if (!parentEmail.trim()) {
      setEmailError('Email is required');
      return;
    }
    setEmailSaving(true);
    try {
      await apiFetch(`/api/admin/players/${id}/update-parent-email`, {
        method: 'POST',
        headers: { 'x-team-id': teamId },
        body: JSON.stringify({ parent_email: parentEmail.trim() }),
      });
      setEmailSuccess('Parent email updated.');
    } catch (err) {
      setEmailError(err.message);
    } finally {
      setEmailSaving(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setPwError(''); setPwSuccess('');
    if (!newPassword) {
      setPwError('Password is required');
      return;
    }
    if (newPassword.length < 6) {
      setPwError('Password must be at least 6 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPwError('Passwords do not match');
      return;
    }
    setPwSaving(true);
    try {
      await apiFetch(`/api/admin/players/${id}/reset-password`, {
        method: 'PUT',
        headers: { 'x-team-id': teamId },
        body: JSON.stringify({ password: newPassword }),
      });
      setPwSuccess('Password reset.');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setPwError(err.message);
    } finally {
      setPwSaving(false);
    }
  };

  if (!teamId) {
    return (
      <div style={{ padding: 20 }}>
        <p style={{ color: 'var(--danger)' }}>Missing team context. Please go back and try again.</p>
        <button className="btn btn-outline btn-sm" onClick={() => navigate('/club')}>Back to Club</button>
      </div>
    );
  }

  if (loading) return <div className="loading"><div className="spinner" /></div>;

  if (!player) {
    return (
      <div style={{ padding: 20 }}>
        <p style={{ color: 'var(--danger)' }}>Player not found.</p>
        <button className="btn btn-outline btn-sm" onClick={() => navigate('/club')}>Back to Club</button>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 500, margin: '0 auto', padding: 20 }}>
      <button className="btn btn-outline btn-sm" onClick={() => navigate('/club')} style={{ marginBottom: 16 }}>
        <i className="fas fa-arrow-left" style={{ marginRight: 6 }}></i> Back to Club
      </button>

      <h2 style={{ fontSize: '1.2rem', marginBottom: 16 }}>Edit Player: {player.first_name} {player.last_name}</h2>

      {/* Player Name */}
      <div className="card" style={{ marginBottom: 16 }}>
        <h3 style={{ fontSize: '1rem', marginBottom: 12 }}>Player Name</h3>
        <form onSubmit={handleSaveName}>
          <div style={{ marginBottom: 10 }}>
            <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: 4 }}>First Name</label>
            <input type="text" className="input" value={firstName} onChange={(e) => setFirstName(e.target.value)} style={{ padding: '10px 12px' }} required />
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: 4 }}>Last Name</label>
            <input type="text" className="input" value={lastName} onChange={(e) => setLastName(e.target.value)} style={{ padding: '10px 12px' }} required />
          </div>
          {nameError && <div style={{ color: 'var(--danger)', fontSize: '0.85rem', marginBottom: 8 }}>{nameError}</div>}
          {nameSuccess && <div style={{ color: 'var(--success)', fontSize: '0.85rem', marginBottom: 8 }}>{nameSuccess}</div>}
          <button type="submit" className="btn btn-orange btn-sm" disabled={nameSaving}>{nameSaving ? 'Saving...' : 'Save'}</button>
        </form>
      </div>

      {/* Parent Email */}
      <div className="card" style={{ marginBottom: 16 }}>
        <h3 style={{ fontSize: '1rem', marginBottom: 12 }}>Parent Email</h3>
        <form onSubmit={handleSaveEmail}>
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: 4 }}>Email</label>
            <input type="email" className="input" value={parentEmail} onChange={(e) => setParentEmail(e.target.value)} style={{ padding: '10px 12px' }} required />
          </div>
          {emailError && <div style={{ color: 'var(--danger)', fontSize: '0.85rem', marginBottom: 8 }}>{emailError}</div>}
          {emailSuccess && <div style={{ color: 'var(--success)', fontSize: '0.85rem', marginBottom: 8 }}>{emailSuccess}</div>}
          <button type="submit" className="btn btn-orange btn-sm" disabled={emailSaving}>{emailSaving ? 'Saving...' : 'Save'}</button>
        </form>
      </div>

      {/* Reset Password */}
      <div className="card" style={{ marginBottom: 16 }}>
        <h3 style={{ fontSize: '1rem', marginBottom: 12 }}>Reset Password</h3>
        <form onSubmit={handleResetPassword}>
          <div style={{ marginBottom: 10 }}>
            <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: 4 }}>New Password</label>
            <input type="password" className="input" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} style={{ padding: '10px 12px' }} placeholder="Min 6 characters" required />
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: 4 }}>Confirm Password</label>
            <input type="password" className="input" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} style={{ padding: '10px 12px' }} required />
          </div>
          {pwError && <div style={{ color: 'var(--danger)', fontSize: '0.85rem', marginBottom: 8 }}>{pwError}</div>}
          {pwSuccess && <div style={{ color: 'var(--success)', fontSize: '0.85rem', marginBottom: 8 }}>{pwSuccess}</div>}
          <button type="submit" className="btn btn-orange btn-sm" disabled={pwSaving}>{pwSaving ? 'Saving...' : 'Reset Password'}</button>
        </form>
      </div>
    </div>
  );
}
